import { setTelemetryBeacon } from '../state/telemetry.js';

/**
 * Builder analytics beacon — every Reader telemetry event (track()) is
 * fire-and-forget POSTed to the Builder's `/api/analytics/event` endpoint.
 * The Builder dispatches to whatever provider is configured server-side
 * (Vercel Analytics by default; GA4 or Splunk HEC when the env vars land).
 *
 * The Reader, ESV deploys, AND every partner-PWA all funnel through the
 * SAME Builder endpoint, so the dashboard answers "who's using my apps"
 * regardless of which deployment served the page.
 *
 * Cross-origin POST via sendBeacon — no preflight, no CORS pain for the
 * happy path. The Builder route also returns the CORS allow-headers for
 * non-Safari browsers that do preflight.
 *
 * Privacy: only the four event types the Builder route accepts
 * (`app_open`, `session_start`, `reader_chapter_view`, `module_open`)
 * actually ship. Anything else from the Reader's local `track()` stays
 * in-memory and is visible only to the dev console via
 * `window.__appDiag()`.
 *
 * Secure mode: when `window.__SECURE_MODE__` is 'restricted' or 'moderate',
 * all outbound telemetry is suppressed entirely.
 */

const SECURE_MODE = typeof window !== 'undefined' ? (window as any).__SECURE_MODE__ : null;
const analyticsDisabled = SECURE_MODE === 'restricted' || SECURE_MODE === 'moderate';

const BUILDER_BASE = (
  (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_BUILDER_BASE_URL ??
  'https://fcbh-app-builder.vercel.app'
).replace(/\/$/, '');

const SHIPPED_NAMES = new Set([
  'app_open',
  'session_start',
  'reader_chapter_view',
  'module_open',
]);

export function installBuilderAnalyticsBeacon(): void {
  if (typeof window === 'undefined') return;
  setTelemetryBeacon((event) => {
    if (analyticsDisabled) return;
    if (!SHIPPED_NAMES.has(event.name)) return;
    const body: Record<string, unknown> = { name: event.name };
    if (event.props) {
      for (const k of ['bookOsis', 'chapter', 'module']) {
        if (typeof event.props[k] !== 'undefined') body[k] = event.props[k];
      }
    }
    const json = JSON.stringify(body);
    try {
      if ('sendBeacon' in navigator) {
        navigator.sendBeacon(
          `${BUILDER_BASE}/api/analytics/event`,
          new Blob([json], { type: 'application/json' }),
        );
        return;
      }
      void fetch(`${BUILDER_BASE}/api/analytics/event`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: json,
        keepalive: true,
      }).catch(() => {});
    } catch {
      // Best-effort — telemetry never blocks the user.
    }
  });
}
